import { useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { FOCUS_RING } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import { HourglassMediumIcon, LockSimpleIcon } from '@phosphor-icons/react'

import { TrialPhase } from '@scrambl/shared'

import type { TrialTone } from './trial-copy'
import { askAnAdminLine, trialChipLabel, trialTone } from './trial-copy'
import { TrialActivationDialog } from './TrialActivationDialog'
import { useWorkspaceTrial } from './useWorkspaceTrial'

const TONE_CLASSES: Record<TrialTone, string> = {
  // Own surface so it reads on the web header and on any title-bar glass.
  quiet: 'border-border bg-background/80 text-foreground hover:bg-muted',
  warning:
    'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-100 dark:hover:bg-amber-500/25',
  destructive:
    'border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15',
}

/**
 * Days left in a no-card trial, in the web header and the desktop title bar
 * (docs/PLAN-no-card-trials.md). Everyone in the workspace sees it; owners
 * and admins click it to add a card, everyone else is told who to ask.
 * Renders nothing for personal spaces and paid or activated workspaces.
 */
export function TrialChip({ className }: { className?: string }) {
  const { trial, isAdmin, organizationId, seats } = useWorkspaceTrial()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  if (!trial || !organizationId) return null

  const tone = trialTone(trial)
  const { label, shortLabel } = trialChipLabel(trial, { isAdmin })
  const Icon =
    trial.phase === TrialPhase.LOCKED ? LockSimpleIcon : HourglassMediumIcon
  const chipClassName = cn(
    'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[length:var(--font-size-sm)] font-medium leading-none transition-colors',
    FOCUS_RING,
    TONE_CLASSES[tone],
    className,
  )
  const content = (
    <>
      <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" weight="bold" />
      {/* Short form until the top bar has room (it otherwise overlaps the
          Timer and Record buttons at tablet and narrow-desktop widths). */}
      <span className="max-lg:hidden">{label}</span>
      <span className="lg:hidden">{shortLabel}</span>
    </>
  )

  if (isAdmin) {
    return (
      <>
        <button
          type="button"
          data-trial-chip
          data-tone={tone}
          aria-label={`${label}. Add a card to activate Scrambl`}
          className={chipClassName}
          onClick={() => setIsDialogOpen(true)}
        >
          {content}
        </button>
        {isDialogOpen && (
          <TrialActivationDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            organizationId={organizationId}
            trial={trial}
            currentSeats={seats}
          />
        )}
      </>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-trial-chip
          data-tone={tone}
          aria-label={label}
          className={chipClassName}
        >
          {content}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <p className="text-[length:var(--font-size-sm)] font-semibold text-foreground">
          Ask an admin to activate Scrambl
        </p>
        <p className="mt-1 text-[length:var(--font-size-sm)] text-muted-foreground">
          {askAnAdminLine(trial.activationContacts)}
        </p>
      </PopoverContent>
    </Popover>
  )
}
